import { DEFAULT_LOCALE, type SupportedLocale } from "@patient-watch/i18n";
import type { DictionaryT, InputT } from "@patient-watch/types";
import type { BranchingScaleBranch } from "@/components/input/custom/branching-scale-options/parseBranchingScaleOptions";
import { resolveInputKey } from "@/features/pages/utils/inputKeyAliases";
import {
    findOptionByValue,
    optionsUnderParent,
    resolveDictionary,
    type ResolvedDictionary,
    type ResolvedOption,
} from "./resolveDictionary";

/**
 * One read path for an input's choices.
 *
 * Inputs used to carry their choices in `options` (text) or `scale_options` (label and value).
 * pages_view now also returns `dictionary` and `option_rows` when an input points at a
 * dictionary_id. `withResolvedChoices` turns those rows back into the legacy fields, so every
 * input component keeps reading `scale_options` or `options` and needs no knowledge of
 * dictionaries. An input with no rows is returned untouched.
 */

/** `scale_options` is settings (min, max, step), not a list of choices. Dictionaries do not apply. */
const SETTINGS_INPUT_KEYS = new Set<string>(["ratings-list", "ms-adl-ratings-list"]);

/** These read the plain `options` list and keep `scale_options` as their settings. */
const OPTIONS_LIST_INPUT_KEYS = new Set<string>(["measurement", "ms-adl-selection"]);

/** Question 8 of each VISA questionnaire: pick a branch (A, B, C), then a duration under it. */
const BRANCHING_INPUT_KEYS = new Set<string>(["visa-a-8", "visa-g-8", "visa-p-8", "visa-h-8"]);

type InputChoiceSource = Pick<InputT, "input_id" | "dictionary_id" | "dictionary" | "option_rows">;

export function hasOptionRows(input: InputChoiceSource): boolean {
    return Array.isArray(input.option_rows) && input.option_rows.length > 0;
}

/** True when the saved answer should be the dictionary's code or value rather than free text. */
export function isDictionaryInput(input: Pick<InputT, "dictionary_id">): boolean {
    return input.dictionary_id != null;
}

function syntheticDictionary(input: InputChoiceSource): DictionaryT {
    return {
        dictionary_id: input.dictionary_id ?? input.input_id,
        created_at: "",
        name: input.input_id,
        description: null,
        placeholder: null,
        search_placeholder: null,
        empty_text: null,
        is_searchable: false,
        is_clearable: false,
        is_multiple: false,
        translations: {},
    };
}

/** Top-level choices for an input, in sort order, with the locale applied. Null when the input has no rows. */
export function resolveInputOptions(
    input: InputChoiceSource,
    locale: SupportedLocale = DEFAULT_LOCALE
): ResolvedOption[] | null {
    if (!hasOptionRows(input)) return null;
    const resolved = resolveDictionary(input.dictionary ?? syntheticDictionary(input), input.option_rows ?? [], locale);
    return optionsUnderParent(resolved.options, null);
}

/** Every option, children included, in sort order with the locale applied. Null when the input has no rows. */
function resolveAllOptions(input: InputChoiceSource, locale: SupportedLocale): ResolvedOption[] | null {
    if (!hasOptionRows(input)) return null;
    return resolveDictionary(input.dictionary ?? syntheticDictionary(input), input.option_rows ?? [], locale).options;
}

/**
 * Branches for a two-level scored question, read from parent_option_id the same way chained lists
 * (area, site, subsite) are. A top-level row is a branch: its label is the branch text and its
 * metadata holds branch_label and duration_prompt. The rows under it are the durations.
 * A duration's value is its points, so the same points can repeat under different branches.
 */
export function resolveBranchingChoices(
    input: InputChoiceSource,
    locale: SupportedLocale = DEFAULT_LOCALE
): BranchingScaleBranch[] | null {
    const all = resolveAllOptions(input, locale);
    if (!all) return null;

    const branches: BranchingScaleBranch[] = [];
    for (const parent of optionsUnderParent(all, null)) {
        const children = optionsUnderParent(all, parent.optionId).filter(
            (child) => typeof child.storedValue === "number"
        );
        if (children.length === 0) continue;
        const metadata = parent.metadata;
        branches.push({
            value: String(parent.storedValue).trim().toLowerCase(),
            label:
                typeof metadata.branch_label === "string" && metadata.branch_label.trim()
                    ? metadata.branch_label.trim()
                    : String(parent.storedValue).toUpperCase(),
            text: parent.label,
            durationPrompt:
                typeof metadata.duration_prompt === "string" && metadata.duration_prompt.trim()
                    ? metadata.duration_prompt.trim()
                    : DEFAULT_DURATION_PROMPT,
            options: children.map((child) => ({ label: child.label, value: child.storedValue as number })),
        });
    }
    return branches.length > 0 ? branches : null;
}

const DEFAULT_DURATION_PROMPT = "Select duration";

/** The same branches in the flat shape the VISA input reads from scale_options. */
function toBranchingScaleOptions(branches: BranchingScaleBranch[]): Record<string, unknown>[] {
    return branches.flatMap((branch) =>
        branch.options.map((option) => ({
            branch: branch.value,
            branch_label: branch.label,
            branch_text: branch.text,
            duration_prompt: branch.durationPrompt,
            label: option.label,
            value: option.value,
        }))
    );
}

/** The dictionary with its control flags and locale copy. Null unless pages_view returned one. */
export function resolveInputDictionary(
    input: InputChoiceSource,
    locale: SupportedLocale = DEFAULT_LOCALE
): ResolvedDictionary | null {
    if (!input.dictionary || !hasOptionRows(input)) return null;
    return resolveDictionary(input.dictionary, input.option_rows ?? [], locale);
}

function toScaleOption(option: ResolvedOption): Record<string, unknown> {
    return {
        ...option.metadata,
        label: option.label,
        value: option.storedValue,
        ...(option.code == null ? {} : { code: option.code }),
        ...(option.description ? { description: option.description } : {}),
        ...(option.disabled ? { disabled: true } : {}),
    };
}

/**
 * Fills `scale_options` and `options` from dictionary or input-owned rows, so components read one shape.
 * Labels follow the locale. Values are exactly what is stored: a coded list keeps its number.
 */
export function withResolvedChoices(input: InputT, locale: SupportedLocale = DEFAULT_LOCALE): InputT {
    const choices = resolveInputOptions(input, locale);
    if (!choices) return input;

    const key = resolveInputKey(input.input_key);
    if (SETTINGS_INPUT_KEYS.has(key)) return input;

    if (BRANCHING_INPUT_KEYS.has(key)) {
        const branches = resolveBranchingChoices(input, locale);
        if (branches) {
            return {
                ...input,
                options: branches.flatMap((branch) => branch.options.map((option) => option.label)),
                scale_options: toBranchingScaleOptions(branches) as unknown as InputT["scale_options"],
            };
        }
    }

    if (key === "measurement") {
        const units = choices.map((choice) =>
            typeof choice.metadata.unit === "string" ? choice.metadata.unit : choice.value
        );
        return { ...input, options: units };
    }

    if (OPTIONS_LIST_INPUT_KEYS.has(key)) {
        return { ...input, options: choices.map((choice) => choice.label) };
    }

    return {
        ...input,
        options: choices.map((choice) => choice.label),
        scale_options: choices.map(toScaleOption) as unknown as InputT["scale_options"],
    };
}

export interface ChoiceEntryFields {
    number_value: number | null;
    text_value: string | null;
}

/**
 * What a single-answer choice saves into entry_values.
 * A number (a code or a score) goes in number_value, with the English label in text_value.
 * A text or boolean value goes in text_value on its own. No selection saves nothing.
 */
export function toChoiceEntryFields(
    options: readonly ResolvedOption[],
    controlValue: string | number | boolean | null | undefined
): ChoiceEntryFields {
    if (controlValue === null || controlValue === undefined || controlValue === "") {
        return { number_value: null, text_value: null };
    }
    const choice = findOptionByValue(options, String(controlValue));
    if (!choice) return { number_value: null, text_value: String(controlValue) };
    if (typeof choice.storedValue === "number") {
        return { number_value: choice.storedValue, text_value: choice.label };
    }
    return { number_value: null, text_value: String(choice.storedValue) };
}

/** The control value to restore from a saved entry: the code when one was saved, else the text. */
export function toChoiceControlValue(entry: { number_value?: number | null; text_value?: string | null }): string {
    if (entry.number_value != null) return String(entry.number_value);
    return entry.text_value ?? "";
}
